import { useState } from "react";

import Button from "@mui/material/Button";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useLoadMore from "hooks/useLoadMore";
import SearchItem from "components/SearchItem";
import WheelTable from "features/mst_wheel/components/WheelTable";
import useWheelList from "features/mst_wheel/hooks/useWheelList";
import { MstItemDetailWheel } from "features/mst_wheel/types";
import WheelFormModal from "features/mst_wheel/components/WheelFormModal";

/**
 * WheelList component displays a list of wheels with pagination and filtering options.
 * It includes a search form, wheel filters, and a table to display wheel details.
 * @example
 * return (
 *   <WheelList />
 * )
 * @returns {React.ReactElement} The rendered component.
 * @remarks
 * - The component uses infinite scrolling to load more wheels as the user scrolls down.
 * - It includes a modal to display detailed information about a selected wheel.
 * - The search form allows filtering wheels by name, email, and type ID.
 * - The component uses Material-UI for styling and layout.
 * @event handleDetailClick - Opens the wheel detail modal with the selected wheel's information.
 * @event handleCloseModal - Closes the wheel detail modal and clears the selected wheel.
 * @event handleCheckbox - Updates the state of the "Available ID only" checkbox.
 * @event handleScroll - Handles the infinite scrolling to load more wheels.
 */
function WheelList() {
  const navigate = useNavigate();
  const {
    wheels,
    queryParams,
    loading,
    total,
    handleSearch,
    handleUpdateWheel,
    setQueryParams,
    onLoadMore,
  } = useWheelList();
  const { renderNodeLoader } = useLoadMore(onLoadMore, loading);

  const [selectedWheel, setSelectedWheel] = useState<MstItemDetailWheel>();
  const [isShowModal, setIsShowModal] = useState(false);

  const openUpdateModal = (wheel: MstItemDetailWheel) => {
    setSelectedWheel(() => wheel);
    setIsShowModal(true);
  };

  const closeUpdateModel = () => {
    setSelectedWheel(undefined);
    setIsShowModal(false);
  };

  const handleSubmitUpdateWheel = async (wheel: MstItemDetailWheel) => {
    await handleUpdateWheel(wheel);
    setSelectedWheel(undefined);
    setIsShowModal(false);
  };

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box sx={{ p: { xs: 2, md: 2.5, lg: 5 } }}>
        <Box
          sx={{
            paddingBottom: "4px",
            display: { sm: "block", md: "flex" },
            gap: "80px",
          }}
        >
          <Box
            sx={{
              textAlign: "right",
              display: { sm: "flex", md: "inline-block" },
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                variant="h4"
                margin={0}
                textAlign="right"
                fontSize={{ xs: "28px", md: "33px" }}
                color="var(--gray-700)"
              >
                マスタ管理
              </Typography>
              <Typography
                variant="h5"
                margin={0}
                textAlign="right"
                fontSize={{ xs: "24px", md: "28px" }}
                color="var(--gray-700)"
              >
                ホイール一覧
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                columnGap: "35px",
                margin: "0 auto",
                marginBottom: "20px",
                flexWrap: "wrap",
                marginTop: "60px",
              }}
            >
              <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <SearchItem
                  label="YH商品コード"
                  type="input"
                  placeholder=""
                  value={queryParams.item_cd || ""}
                  onChange={(v) => setQueryParams({ ...queryParams, item_cd: String(v ?? "") })}
                  containerSx={{ minWidth: 0, width: { xs: "100%", lg: "auto" } }}
                  labelSx={{ px: "40px", minWidth: 150 }}
                  valueSx={{ width: { sm: "100%", xl: "200px" } }}
                />
                <SearchItem
                  label="メーカー名"
                  type="input"
                  placeholder=""
                  value={queryParams.maker_name || ""}
                  onChange={(v) => setQueryParams({ ...queryParams, maker_name: String(v ?? "") })}
                  containerSx={{ minWidth: 0, width: { xs: "100%", lg: "auto" } }}
                  labelSx={{ px: "40px", minWidth: 150 }}
                  valueSx={{ width: { sm: "100%", xl: "200px" } }}
                />
                <SearchItem
                  label="ブランド名"
                  type="input"
                  placeholder=""
                  value={queryParams.brand_name || ""}
                  onChange={(v) => setQueryParams({ ...queryParams, brand_name: String(v ?? "") })}
                  containerSx={{ minWidth: 0, width: { xs: "100%", lg: "auto" } }}
                  labelSx={{ px: "40px", minWidth: 150 }}
                  valueSx={{ width: { sm: "100%", xl: "200px" } }}
                />
              </Box>
            </Box>
            <Box textAlign="center" margin="0 auto" sx={{ backgroundColor: "var(--white)" }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "var(--gray-500)",
                  color: "var(--white)",
                  width: "190px",
                  height: "50px",
                  fontSize: "25px",
                  marginBottom: "20px",
                  borderRadius: "9px",
                  ":hover": {
                    backgroundColor: "var(--gray-250)",
                    boxShadow: "var(--shadow)",
                  },
                }}
                onClick={handleSearch}
              >
                検索
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          px: { xs: 2, md: 2.5, lg: 5 },
          display: "flex",
          backgroundColor: "var(--offwhite-100)",
          py: 3,
          justifyContent: "center",
          flexDirection: "column",
          gap: "1.5",
        }}
      >
        <Box display="flex" justifyContent="end" fontSize="20px" marginBottom="15px">
          {`件数【${total}件】`}
        </Box>
        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <WheelTable wheels={wheels} openModal={openUpdateModal} />
          {renderNodeLoader()}
        </Box>
      </Box>

      {selectedWheel && (
        <WheelFormModal
          type="update"
          visible={isShowModal}
          onClose={closeUpdateModel}
          onHandleSubmit={handleSubmitUpdateWheel}
          isLoading={loading}
          wheelDetail={selectedWheel}
        />
      )}
    </>
  );
}

export default WheelList;
